import _ from 'lodash'

import { CreditCard } from "./CreditCard";
import { RawCreditCardData } from "./RawCreditCardData";
import { EditedRawCreditCardData } from "./EditedRawCreditCardData";
import { RawCardData, type CardData } from "../types/CardData";
import { CategoryTree } from '../types/CardRewardsEarnRates';
import { RawRewardsEarnRate } from '../types/RawRewardsEarnRates';
import { CreditCardRewardsType } from './CreditCardRewardsTypes';

// Should I use overrides or feed data back into the scraper when making changes?

// If the data doesn't need to be updated much, I can just use overrides.
// If the data needs to be updated frequently, I should feed data back into the scraper.

// For now, I'll use overrides.

const CardOverrides: Partial<Record<CreditCard, Partial<RawCardData>>> = {
  [CreditCard.LL_BEAN_MASTERCARD]: {
    imageName: 'll-bean-mastercard.png',
    name: 'LL Bean Mastercard®',
  },
  [CreditCard.DISCOVER_IT_STUDENT_CASH_BACK_CREDIT_CARD]: {
    imageName: 'discover-it-student-cash-back-card.jpeg',
  },
  [CreditCard.CHASE_FREEDOM_FLEX_CREDIT_CARD]: {
    rewardsType: CreditCardRewardsType.CHASE_UR,
  },
  [CreditCard.CHASE_FREEDOM_UNLIMITED_CREDIT_CARD]: {
    rewardsType: CreditCardRewardsType.CHASE_UR,
  },
  [CreditCard.CITI_CUSTOM_CASH_CARD]: {
    imageName: 'citi-custom-cash-card.png',
    name: 'Citi Custom Cash® Card	'
  },
  [CreditCard.CITI_DOUBLE_CASH_CARD]: {
    imageName: 'citi-double-cash-card.png',
  },
  [CreditCard.CITI_SIMPLICITY_CREDIT_CARD]: {
    imageName: 'citi-simplicity-credit-card.png',
    name: 'Citi Simplicity® Credit Card',
  },
  [CreditCard.CITI_STRATA_PREMIER_CARD]: {
    imageName: 'citi-strata-premier-card.png',
  },
  [CreditCard.CITI_SECURED_MASTERCARD]: {
    imageName: 'citi-secured-mastercard.png',
    name: 'Citi® Secured Mastercard®',
  },
  [CreditCard.CITI__AADVANTAGE_BUSINESS_WORLD_ELITE_MASTERCARD]: {
    name: 'Citi® AAdvantage® Business World Elite Mastercard®',
    imageName: 'citi-aadvantage-business-world-elite-mastercard.webp',
  },
  [CreditCard.AMERICAN_AIRLINES_AADVANTAGE_PLATINUM_SELECT_WORLD_ELITE_MASTERCARD]:
    {
      name: 'American Airlines® AAdvantage® Platinum Select® World Elite Mastercard®',
      imageName:
        'american-airlines-aadvantage-platinum-select-world-elite-mastercard.webp',
    },
  [CreditCard.AMERICAN_AIRLINES_AADVANTAGE_MILEUP_CARD]: {
    name: 'American Airlines® AAdvantage® MileUp® Card',
  },
  [CreditCard.ATT_POINTS_PLUS_CARD_FROM_CITI]: {
    name: 'AT&T® Points Plus Card from Citi',
    imageName: 'att-points-plus-card-from-citi.png',
  },
  [CreditCard.BEST_FRIENDS_CREDIT_CARD]: {
    name: 'Best Friends Credit Card',
    imageName: 'best-friends-credit-card.avif',
  },
  [CreditCard.CARECREDIT_CREDIT_CARD]: {
    imageName: 'carecredit-credit-card.webp',
  },
  [CreditCard.COSTCO_ANYWHERE_VISA_CARD_BY_CITI]: {
    imageName: 'costco-anywhere-visa-card-by-citi.jpeg',
    name: 'Costco Anywhere Visa® Card by Citi',
  },
  [CreditCard.COSTCO_ANYWHERE_VISA_BUSINESS_CARD_BY_CITI]: {
    name: 'Costco Anywhere Visa® Business Card by Citi',
    imageName: 'costco-anywhere-visa-business-card-by-citi.png',
  },
  [CreditCard.EVERGREENBY_FNBO_CREDIT_CARD]: {
    name: 'Evergreen by FNBO Credit Card',
    imageName: 'evergreenby-fnbo-credit-card.png',
  },
  [CreditCard.GETAWAY_BY_FNBOCREDIT_CARD]: {
    imageName: 'getaway-by-fnbocredit-card.png',
  },
  [CreditCard.THE_HOME_DEPOT_CONSUMER_CREDIT_CARD]: {
    imageName: 'the-home-depot-consumer-credit-card.png',
    name: 'The Home Depot Consumer Credit Card',
  },
  [CreditCard.MY_GM_REWARDS_MASTERCARD]: {
    imageName: 'my-gm-rewards-mastercard.png',
  },
  [CreditCard.PAYPAL_CREDIT]: {
    imageName: 'paypal-credit.png',
  },
  [CreditCard.SAMS_CLUB_CREDIT_CARD]: {
    name: 'Sams Club Credit Card',
    imageName: 'sams_club_credit_card.webp',
  },
  [CreditCard.SECURED_VISACREDIT_CARD]: {
    imageName: 'secured-visa-credit-card.png',
  },
  [CreditCard.THE_PLATINUM_CARD]: {
    imageName: 'the-platinum-card.png',
  },
  [CreditCard.BUSINESS_PLATINUM_CARD]: {
    imageName: 'business-platinum.avif',
  },
  [CreditCard.AMERICAN_EXPRESS_GOLD_CARD]: {
    imageName: 'american-express-gold-card.png',
  },
  [CreditCard.HILTON_HONORS_AMERICAN_EXPRESS_ASPIRE_CARD]: {
    imageName: 'hilton-honors-american-express-aspire-card.avif',
  },
  [CreditCard.HILTON_HONORS_AMERICAN_EXPRESS_CARD]: {
    imageName: 'hilton-honors-american-express-card.avif',
  },
  [CreditCard.HILTON_HONORS_AMERICAN_EXPRESS_SURPASS_CARD]: {
    imageName: 'hilton-honors-american-express-surpass-card.avif',
  },
  [CreditCard.AMERICAN_EXPRESS_GREEN_CARD]: {
    imageName: 'american-express-green-card.jpeg',
  },
  [CreditCard.DELTA_SKYMILES_BLUE_AMERICAN_EXPRESS_CARD]: {
    imageName: 'delta-skymiles-blue-american-express-card.webp',
  },
  [CreditCard.DELTA_SKYMILES_GOLD_AMERICAN_EXPRESS_CARD]: {
    imageName: 'delta-skymiles-gold-american-express-card.avif',
  },
  [CreditCard.DELTA_SKYMILES_PLATINUM_AMERICAN_EXPRESS_CARD]: {
    imageName: 'delta-skymiles-platinum-american-express-card.avif',
  },
  [CreditCard.DELTA_SKYMILES_RESERVE_AMERICAN_EXPRESS_CARD]: {
    imageName: 'delta-skymiles-reserve-american-express-card.avif',
  },
  [CreditCard.BLUE_CASH_PREFERRED_CARD] : {
    imageName: 'blue-cash-preferred-card.avif',
  },
  [CreditCard.BUSINESS_GOLD_CARD]: {
    imageName: 'business-gold-card.avif',
  },
  [CreditCard.APPLE_CARD]: {
    imageName: 'apple-card.png',
  },
  [CreditCard.BLUE_CASH_EVERYDAY_CARD]: {
    name: 'Blue Cash Everyday Card',
    imageName: 'blue-cash-everyday-card.avif',
    rewardsType: CreditCardRewardsType.CASH_BACK,
  },
  [CreditCard.MARRIOTT_BONVOY_BEVY_AMERICAN_EXPRESS_CARD]: {
    imageName: 'marriott-bonvoy-bevy-american-express-card.avif',
  },
  [CreditCard.MARRIOTT_BONVOY_BRILLIANT_AMERICAN_EXPRESS_CARD]: {
    imageName: 'marriott-bonvoy-brilliant-american-express-card.avif',
  },
  [CreditCard.MATTRESS_FIRM_CREDIT_CARD]: {
    imageName: 'mattress-firm-credit-card.png',
  },
  [CreditCard.MYLOWES_REWARDS_CREDIT_CARD]: {
    imageName: 'mylowes-rewards-credit-card.png',
  },
  [CreditCard.MYWALGREENS_CREDIT_CARD]: {
    imageName: 'mywalgreens-rewards-credit-card.png',
  },
  [CreditCard.ONE_KEY_CARD]: {
    imageName: 'one_key_card.jpeg',
    name: 'One Key™ Card',
  },
  [CreditCard.PAYPAL_CASHBACK_WORLD_MASTERCARD]: {
    imageName: 'paypal-cashback-world-mastercard.jpg',
  },
  [CreditCard.SHELL__FUEL_REWARDS_CREDIT_CARDS]: {
    imageName: 'shell-fuel-rewards-credit-card.png',
    name: 'Shell Fuel Rewards® Credit Card',
  },
  [CreditCard.VENMO_VISA_CREDIT_CARD]: {
    imageName: 'venmo-visa-credit-card.jpeg',
  },
  [CreditCard.VERIZON_VISA_CARD]: {
    imageName: 'verizon-visa-card.jpeg',
  },
  [CreditCard.SYNCHRONY_CAR_CARE_CREDIT_CARD]: {
    imageName: 'synchrony-car-care-credit-card.png',
  },
  [CreditCard.SYNCHRONY_HOME_CREDIT_CARD]: {
    imageName: 'synchrony-home-credit-card.png',
  },
  [CreditCard.SYNCHRONY_HOME_IMPROVEMENT_CREDIT_CARD]: {
    imageName: 'synchrony-home-improvement-credit-card.png',
  },
  [CreditCard.SYNCHRONY_LUXURY_CREDIT_CARD]: {
    imageName: 'synchrony-luxury-credit-card.png',
  },
   [CreditCard.SYNCHRONY_MUSIC__SOUND_CREDIT_CARD]: {
    imageName: 'synchrony-music-sound-credit-card.png',
  }, 
  [CreditCard.SYNCHRONY_OUTDOORS_CREDIT_CARD]: {
    imageName: 'synchrony-outdoors-credit-card.png',
  },
  [CreditCard.SYNCHRONY_PREMIER_WORLD_MASTERCARD]: {
    imageName: 'synchrony-premier-world-mastercard.png',
  },
  [CreditCard.SYNCHRONY_SAMS_CLUB_CREDIT_CARD]: {
    imageName: 'synchrony-sams-club-credit-card.png',
  },
  [CreditCard.SPARK_15_CASH_SELECT]: {
    imageName: 'spark-15-cash-select.avif',
  },
  [CreditCard.SPARK_15X_MILES_SELECT]: {
    imageName: 'spark-15x-miles-select.avif',
  },
  [CreditCard.SPARK_CLASSIC]: {
    imageName: 'spark-classic.png',
  }, 
  [CreditCard.SPARK_2X_MILES]: {
    imageName: 'spark-2x-miles.avif',
  },
  [CreditCard.SPARK_2_CASH]: {
    imageName: 'spark-2-cash.webp',
  },
  [CreditCard.SPARK_2_CASH_PLUS]: {
    imageName: 'spark-2-cash-plus.avif',
  },
};

const ExcludedCards = [
  CreditCard.GM_BUSINESS_MASTERCARD,
  CreditCard.GM_EXTENDED_FAMILY_MASTERCARD,
  CreditCard.GOOGLE_STORE_FINANCING,
  CreditCard.MY_GM_REWARDS_MASTERCARD,
  CreditCard.PAYPAL_CREDIT,
  CreditCard.QCARD_CREDIT_CARD,
  CreditCard.SECURED_VISACREDIT_CARD,
  CreditCard.TSC_CREDIT_CARDS,
  CreditCard.THE_HOME_DEPOT_CONSUMER_CREDIT_CARD,
  CreditCard.MY_BEST_BUY_CREDIT_CARDS,
  CreditCard.SPORTING_GOODS_CREDIT_CARDS,
  CreditCard.SYNCHRONY_MUSIC__SOUND_CREDIT_CARD,
  CreditCard.SYNCHRONY_SAMS_CLUB_CREDIT_CARD,
  CreditCard.SYNCHRONY_PREMIER_WORLD_MASTERCARD,
  CreditCard.SYNCHRONY_OUTDOORS_CREDIT_CARD,
  CreditCard.SYNCHRONY_LUXURY_CREDIT_CARD,
  CreditCard.SYNCHRONY_HOME_CREDIT_CARD,
  CreditCard.SYNCHRONY_HOME_IMPROVEMENT_CREDIT_CARD,
  CreditCard.SYNCHRONY_CAR_CARE_CREDIT_CARD
];

const AnnotatedRawCreditCardData = Object.entries(_.omit(EditedRawCreditCardData  , ExcludedCards)).reduce((acc, [key, value]) => {
  return {
    ...acc,
    [key]: {
      ...value as object,
      ...CardOverrides[key as CreditCard],
    }
  }
}, {} as Record<CreditCard, RawCardData>)

export const CreditCardData = Object.entries(AnnotatedRawCreditCardData).reduce((acc, [key, value]) => {
  return {
    ...acc,
        [key]: {
          ...value,
          rewards_earn_rates: value.rewardsEarnRates?.reduce(
            (acc: CategoryTree<number>, earn_rate: RawRewardsEarnRate) => {
              if (earn_rate.earnRate && (!_.get(acc, earn_rate.category)
                //  || _.get(acc, earn_rate.category) < earn_rate.earnRate
                )) {
                return _.set(acc, earn_rate.category, earn_rate.earnRate)
              } else {
                return acc
              }
            },
            {} as CategoryTree<number>
          ) satisfies CategoryTree<number> | undefined
        }
  }
}, {} as Record<CreditCard, CardData>)
